import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Calendar, AlertCircle, CheckCircle, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { authFetch } from '../utils/authUtils';

const UsageLimitBanner = () => {
  const navigate = useNavigate();
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUsageStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsageStats = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/usage/stats`);
      const data = await response.json();
      setUsageStats(data);
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usageStats) return null;

  const daysRemaining = usageStats.days_limit - usageStats.days_used;
  const isLimitExceeded = !usageStats.can_access;
  const todayExhausted = usageStats.today_remaining === 0;

  return (
    <Card className={`border-2 ${
      isLimitExceeded || todayExhausted
        ? 'bg-red-900/20 border-red-600'
        : daysRemaining <= 2
        ? 'bg-yellow-900/20 border-yellow-600'
        : 'bg-slate-800/50 border-slate-700'
    }`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          {isLimitExceeded || todayExhausted ? (
            <AlertCircle className="w-5 h-5 text-red-400" />
          ) : (
            <Clock className="w-5 h-5 text-indigo-400" />
          )}
          <span className="text-white">Usage Limits</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Monthly Days Usage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Days Used This Month
            </span>
            <span className={`font-semibold ${
              isLimitExceeded ? 'text-red-400' :
              daysRemaining <= 2 ? 'text-yellow-400' :
              'text-white'
            }`}>
              {usageStats.days_used} / {usageStats.days_limit} days
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                isLimitExceeded ? 'bg-red-500' :
                daysRemaining <= 2 ? 'bg-yellow-500' :
                'bg-indigo-500'
              }`}
              style={{ width: `${(usageStats.days_used / usageStats.days_limit) * 100}%` }}
            />
          </div>
          {daysRemaining > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
            </p>
          )}
        </div>

        {/* Daily Time Usage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Today's Reading Time
            </span>
            <span className={`font-semibold ${
              todayExhausted ? 'text-red-400' : 'text-white'
            }`}>
              {usageStats.today_minutes} / {usageStats.daily_limit} min
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                todayExhausted ? 'bg-red-500' : 'bg-green-500'
              }`}
              style={{ width: `${(usageStats.today_minutes / usageStats.daily_limit) * 100}%` }}
            />
          </div>
          {usageStats.today_remaining > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {usageStats.today_remaining} minutes left today
            </p>
          )}
        </div>

        {/* Status Messages */}
        {isLimitExceeded && (
          <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg">
            <p className="text-sm text-red-300 font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Monthly Limit Reached
            </p>
            <p className="text-xs text-red-400 mt-1 mb-3">
              You've used all {usageStats.days_limit} days this month. Upgrade to Premium for unlimited access!
            </p>
            <Button
              onClick={() => navigate('/premium')}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 flex items-center justify-center gap-2"
            >
              <Crown className="w-4 h-4" />
              Go Premium - Unlimited Access
            </Button>
          </div>
        )}

        {todayExhausted && !isLimitExceeded && (
          <div className="p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg">
            <p className="text-sm text-yellow-300 font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Daily Limit Reached
            </p>
            <p className="text-xs text-yellow-400 mt-1 mb-3">
              You've reached today's {usageStats.daily_limit}-minute limit. Upgrade to read without limits!
            </p>
            <Button
              onClick={() => navigate('/premium')}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 flex items-center justify-center gap-2"
            >
              <Crown className="w-4 h-4" />
              Go Premium
            </Button>
          </div>
        )}

        {!isLimitExceeded && !todayExhausted && daysRemaining <= 2 && (
          <div className="p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg">
            <p className="text-xs text-yellow-300">
              ⚠️ Only {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left this month
            </p>
          </div>
        )}

        {!isLimitExceeded && !todayExhausted && daysRemaining > 2 && (
          <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg">
            <p className="text-xs text-green-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              You're all set! Happy reading.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageLimitBanner;
